<template>
  <div class="vc-voice-tone">
    <!-- 服务版本：在模型与语音滚动区内吸顶 -->
    <div class="vc-service-version-pin">
    <div class="vc-section vc-card">
      <div class="vc-section-head vc-section-head--model">
        <span class="vc-section-head__icon vc-section-head__icon--share vc-section-head__icon--chip" aria-hidden="true">
          <ShareAltOutlinedIcon :size="16" />
        </span>
        <span class="vc-section-head__title">服务版本</span>
      </div>
      <div class="vc-call-mode-cards">
        <div
          v-for="item in visibleServiceVersionCards"
          :key="item.value"
          class="vc-call-mode-card"
          :class="{ 'vc-call-mode-card--active': serviceVersion === item.value }"
          @click="setServiceVersion(item.value)"
        >
          <span
            class="vc-call-mode-card__icon"
            :class="'vc-call-mode-card__icon--' + item.iconTone"
            aria-hidden="true"
          >
            <SketchOutlinedIcon v-if="item.icon === 'sketch'" :size="22" />
            <CrownFilledIcon v-else-if="item.icon === 'crown'" :size="22" />
            <StandardCubeIcon v-else :size="22" />
          </span>
          <div class="vc-call-mode-card__text">
            <div class="vc-call-mode-card__title-row">
              <span class="vc-call-mode-card__title">{{ item.title }}</span>
              <span
                class="vc-fake-radio"
                :class="{ 'vc-fake-radio--on': serviceVersion === item.value }"
              />
            </div>
            <p class="vc-call-mode-card__desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- 至尊版：两个千问模型 + 音色 -->
    <div v-if="isSupreme" class="vc-section vc-card">
      <div class="vc-section-head vc-section-head--resource">
        <span class="vc-section-head__icon vc-section-head__icon--resource" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
            <ellipse cx="12" cy="6.2" rx="6.4" ry="2.35" stroke="currentColor" stroke-width="1.6" />
            <path
              d="M5.6 6.2v11.4c0 1.3 2.87 2.35 6.4 2.35s6.4-1.05 6.4-2.35V6.2"
              stroke="currentColor"
              stroke-width="1.6"
            />
            <path
              d="M5.6 11.7c0 1.3 2.87 2.35 6.4 2.35s6.4-1.05 6.4-2.35"
              stroke="currentColor"
              stroke-width="1.6"
            />
          </svg>
        </span>
        <span class="vc-section-head__title">至尊版资源配置</span>
      </div>
      <div class="vc-form-field vc-form-field--resource-line">
        <label class="vc-form-label">资源</label>
        <Select
          v-model="supremeResourceKey"
          class="vc-form-control vc-model-select"
          transfer
          transfer-class-name="vc-model-select-transfer"
        >
          <Option
            v-for="item in supremeResourceOptions"
            :key="item.value"
            :value="item.value"
            :label="item.label"
          >
            {{ item.label }}
          </Option>
        </Select>
      </div>
    </div>

    <div v-if="isSupreme" class="vc-section vc-card">
      <div class="vc-section-head vc-section-head--voice">
        <span class="vc-section-head__icon vc-section-head__icon--voice" aria-hidden="true">
          <svg
            class="vc-voice-params-mic-icon"
            viewBox="0 0 20 20"
            focusable="false"
            width="20"
            height="20"
            fill="none"
            aria-hidden="true"
          >
            <rect x="7.25" y="2" width="5.5" height="9.5" rx="2.75" fill="currentColor" />
            <path
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
              d="M4.5 9.25a5.5 5.5 0 0 0 11 0"
            />
            <path
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
              d="M10 14.75v2.25M7 17h6"
            />
          </svg>
        </span>
        <span class="vc-section-head__title">音色与参数</span>
      </div>

      <div class="vc-form-field">
        <label class="vc-form-label vc-form-label--required">音色</label>
        <div class="vc-voice-select-row">
          <Select
            v-model="voiceCall.presetVoiceId"
            class="vc-form-control vc-voice-preset-select"
            transfer
            transfer-class-name="vc-voice-preset-select-transfer"
            filterable
          >
            <OptionGroup label="系统预设">
              <Option
                v-for="p in systemPresetVoiceOptions"
                :key="'preset-' + p.id"
                :value="p.id"
                :label="p.label"
              >
                {{ p.label }}
              </Option>
            </OptionGroup>
            <OptionGroup v-if="clonedVoiceOptions.length" label="我的音色">
              <Option
                v-for="p in clonedVoiceOptions"
                :key="'clone-' + p.id"
                :value="p.id"
                :label="p.label"
              >
                {{ p.label }}
              </Option>
            </OptionGroup>
          </Select>
          <Button class="vc-my-voices-btn" @click="$emit('open-my-voices')">
            <span class="vc-my-voices-btn__content">
              <Icon type="ios-mic-outline" size="16" />
              <span class="vc-my-voices-btn__label">我的音色</span>
            </span>
          </Button>
        </div>
      </div>

      <SpeechParamsSection :speech-params="voiceCall.speechParams" :max-params="maxSpeechParams" />
    </div>

    <!-- 旗舰版 / 标准版：大模型下拉 + 接入配置 -->
    <template v-else>
      <div class="vc-section vc-card">
        <div class="vc-section-head vc-section-head--resource">
          <span class="vc-section-head__icon vc-section-head__icon--resource" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
              <ellipse cx="12" cy="6.2" rx="6.4" ry="2.35" stroke="currentColor" stroke-width="1.6" />
              <path
                d="M5.6 6.2v11.4c0 1.3 2.87 2.35 6.4 2.35s6.4-1.05 6.4-2.35V6.2"
                stroke="currentColor"
                stroke-width="1.6"
              />
              <path
                d="M5.6 11.7c0 1.3 2.87 2.35 6.4 2.35s6.4-1.05 6.4-2.35"
                stroke="currentColor"
                stroke-width="1.6"
              />
            </svg>
          </span>
          <span class="vc-section-head__title">{{ resourceConfigTitle }}</span>
        </div>
        <div class="vc-form-field vc-form-field--resource-line">
          <label class="vc-form-label">资源</label>
          <Select
            v-model="modelSelectionKey"
            class="vc-form-control vc-model-select"
            transfer
            transfer-class-name="vc-model-select-transfer"
          >
            <Option
              v-for="item in modelSelectOptions"
              :key="item.value"
              :value="item.value"
              :label="item.label"
            >
              {{ item.label }}
            </Option>
          </Select>
        </div>
      </div>

      <div class="vc-section vc-card">
        <div class="vc-section-head vc-section-head--access">
          <span class="vc-section-head__icon vc-section-head__icon--access" aria-hidden="true">
            <Icon type="ios-git-network" size="20" />
          </span>
          <span class="vc-section-head__title">接入配置</span>
        </div>
        <TripleAccessSection
          :triple-access="activeAccess"
          :product-line="activeProductLine"
          :agora-resources="isFlagship"
          :cloned-voices="voiceCall.clonedVoices"
          :tts-speech-params="activeTtsSpeechParams"
          :asr-speech-params="activeAsrSpeechParams"
          :max-speech-params="maxSpeechParams"
          :max-my-resources="maxMyVoices"
          @open-my-voices="$emit('open-my-voices')"
        />
      </div>
    </template>
  </div>
</template>

<script>
import TripleAccessSection from './TripleAccessSection.vue'
import SpeechParamsSection from './SpeechParamsSection.vue'
import ShareAltOutlinedIcon from '../shared/ShareAltOutlinedIcon.vue'
import SketchOutlinedIcon from '../shared/SketchOutlinedIcon.vue'
import CrownFilledIcon from '../shared/CrownFilledIcon.vue'
import StandardCubeIcon from '../shared/StandardCubeIcon.vue'
import {
  PRESET_MODE,
  SERVICE_VERSION,
  applyFlagshipS1ToAccess,
  applySystemLlmCatalogToTripleAccess,
  buildFlagshipModelSelectOptions,
  buildFlagshipS1SelectOption,
  createDefaultFlagshipAccess,
  createDefaultTripleAccess,
  parseFlagshipModelSelectionKey,
  parseSystemLlmModelIdFromResource,
  resolveServiceVersion,
  resolveTripleAccessProductLine,
} from '../../utils/tripleAccessHelpers'
import { createDefaultSpeechParams } from '../../utils/flowSectionHelpers'
import {
  VOICE_ENGINE,
  getDefaultPresetId,
  getPresetVoiceOptions,
  getPresetVoicesByEngine,
  clonedVoicesToPresetOptions,
} from '../../constants/voicePresets'

const MODEL_SELECTION = Object.freeze({
  ENGINE_ALIYUN: 'engine:aliyun',
  ENGINE_QWEN_OMNI: 'engine:qwen_omni',
})

const MODEL_OPTION_LABELS = Object.freeze({
  [MODEL_SELECTION.ENGINE_ALIYUN]: '千问全模态3.5-Omni',
  [MODEL_SELECTION.ENGINE_QWEN_OMNI]: '千问实时语音Audio3.0-Plus',
})

export default {
  name: 'VoiceToneSection',
  components: {
    TripleAccessSection,
    SpeechParamsSection,
    ShareAltOutlinedIcon,
    SketchOutlinedIcon,
    CrownFilledIcon,
    StandardCubeIcon,
  },
  props: {
    voiceCall: {
      type: Object,
      required: true,
    },
    maxMyVoices: {
      type: Number,
      default: 5,
    },
    maxSpeechParams: {
      type: Number,
      default: 15,
    },
  },
  data() {
    return {
      VOICE_ENGINE,
      PRESET_MODE,
      SERVICE_VERSION,
      serviceVersionCards: [
        {
          value: SERVICE_VERSION.SUPREME,
          title: '至尊版',
          desc: '交互自然，无延迟，适合比较复杂场景，行业领先',
          icon: 'crown',
          iconTone: 'flagship',
        },
        {
          value: SERVICE_VERSION.FLAGSHIP,
          title: '旗舰版',
          desc: '交互自然，全程延迟约800毫秒，适合相对复杂场景，行业领先',
          icon: 'sketch',
          iconTone: 'supreme',
        },
        {
          value: SERVICE_VERSION.STANDARD,
          title: '标准版',
          desc: '交互自然，全程延迟约1秒，适合大部分场景，行业领先',
          icon: 'cube',
          iconTone: 'standard',
        },
      ],
    }
  },
  computed: {
    presetVoices() {
      return getPresetVoiceOptions(this.voiceCall.voiceEngine, this.voiceCall.clonedVoices)
    },
    systemPresetVoiceOptions() {
      return getPresetVoicesByEngine(this.voiceCall.voiceEngine)
    },
    clonedVoiceOptions() {
      return clonedVoicesToPresetOptions(this.voiceCall.clonedVoices, this.voiceCall.voiceEngine)
    },
    serviceVersion() {
      return resolveServiceVersion({
        serviceVersion: this.voiceCall.serviceVersion,
        presetMode: this.voiceCall.presetMode,
      })
    },
    visibleServiceVersionCards() {
      const fee = this.voiceCall.feeVersions
      const withoutFlagship = this.serviceVersionCards.filter(
        (card) => card.value !== SERVICE_VERSION.FLAGSHIP,
      )
      if (!Array.isArray(fee) || !fee.length) return withoutFlagship
      const allowed = new Set(fee.map((item) => item && item.cls))
      const visible = this.serviceVersionCards.filter((card) => allowed.has(card.value))
      return visible.length ? visible : withoutFlagship
    },
    isSupreme() {
      return this.serviceVersion === SERVICE_VERSION.SUPREME
    },
    isFlagship() {
      return this.serviceVersion === SERVICE_VERSION.FLAGSHIP
    },
    isStandard() {
      return this.serviceVersion === SERVICE_VERSION.STANDARD
    },
    supremeResourceOptions() {
      return [
        { value: MODEL_SELECTION.ENGINE_ALIYUN, label: MODEL_OPTION_LABELS[MODEL_SELECTION.ENGINE_ALIYUN] },
        { value: MODEL_SELECTION.ENGINE_QWEN_OMNI, label: MODEL_OPTION_LABELS[MODEL_SELECTION.ENGINE_QWEN_OMNI] },
      ]
    },
    supremeResourceKey: {
      get() {
        const engine = String(this.voiceCall.voiceEngine || VOICE_ENGINE.ALIYUN).trim()
        if (engine === VOICE_ENGINE.QWEN_OMNI) return MODEL_SELECTION.ENGINE_QWEN_OMNI
        return MODEL_SELECTION.ENGINE_ALIYUN
      },
      set(key) {
        const engine =
          key === MODEL_SELECTION.ENGINE_QWEN_OMNI ? VOICE_ENGINE.QWEN_OMNI : VOICE_ENGINE.ALIYUN
        if (this.voiceCall.voiceEngine !== engine) {
          this.$set(this.voiceCall, 'voiceEngine', engine)
        }
        this.$nextTick(() => this.syncPresetVoiceIdToEngine())
      },
    },
    standardProductLine() {
      return resolveTripleAccessProductLine(this.voiceCall.tripleAccess && this.voiceCall.tripleAccess.llm)
    },
    standardModelId() {
      const llm = this.voiceCall.tripleAccess && this.voiceCall.tripleAccess.llm
      return (
        parseSystemLlmModelIdFromResource(llm && llm.resource) ||
        String((llm && llm.model) || '').trim()
      )
    },
    standardModelOptions() {
      return buildFlagshipModelSelectOptions(this.isStandard ? this.standardModelId : '')
    },
    flagshipSelectOption() {
      return buildFlagshipS1SelectOption()
    },
    activeAccess() {
      return this.isFlagship ? this.voiceCall.flagshipAccess : this.voiceCall.tripleAccess
    },
    activeTtsSpeechParams() {
      return this.isFlagship ? this.voiceCall.flagshipTtsSpeechParams : this.voiceCall.ttsSpeechParams
    },
    activeAsrSpeechParams() {
      return this.isFlagship ? this.voiceCall.flagshipAsrSpeechParams : this.voiceCall.asrSpeechParams
    },
    activeProductLine() {
      return this.isFlagship ? '' : this.standardProductLine
    },
    modelSelectOptions() {
      if (this.isFlagship) return [this.flagshipSelectOption]
      return this.standardModelOptions
    },
    modelSelectionKey: {
      get() {
        if (this.isFlagship) return this.flagshipSelectOption.value
        const hit = this.standardModelOptions.find((item) => item.modelId === this.standardModelId)
        return hit ? hit.value : this.standardModelOptions[0] && this.standardModelOptions[0].value
      },
      set(key) {
        if (this.isFlagship) {
          this.ensureFlagshipAccess()
          applyFlagshipS1ToAccess(this.voiceCall.flagshipAccess)
          return
        }
        const modelId = parseFlagshipModelSelectionKey(key)
        if (!modelId) return
        this.applyStandardModel(modelId)
      },
    },
    resourceConfigTitle() {
      return this.isFlagship ? '旗舰版资源配置' : '标准版资源配置'
    },
  },
  watch: {
    'voiceCall.voiceEngine'() {
      this.$nextTick(() => this.syncPresetVoiceIdToEngine())
    },
    'voiceCall.clonedVoices': {
      handler() {
        this.$nextTick(() => this.syncPresetVoiceIdToEngine())
      },
      deep: true,
    },
  },
  created() {
    if (!this.voiceCall.presetMode) {
      this.$set(this.voiceCall, 'presetMode', PRESET_MODE.ENGINE)
    }
    if (!this.voiceCall.serviceVersion) {
      this.$set(
        this.voiceCall,
        'serviceVersion',
        resolveServiceVersion({ presetMode: this.voiceCall.presetMode }),
      )
    }
    if (!this.voiceCall.tripleAccess || typeof this.voiceCall.tripleAccess !== 'object') {
      this.$set(this.voiceCall, 'tripleAccess', createDefaultTripleAccess())
    }
    this.ensureFlagshipAccess()
    if (!Array.isArray(this.voiceCall.speechParams)) {
      this.$set(this.voiceCall, 'speechParams', createDefaultSpeechParams())
    }
    if (!Array.isArray(this.voiceCall.ttsSpeechParams)) {
      this.$set(this.voiceCall, 'ttsSpeechParams', createDefaultSpeechParams())
    }
    if (!Array.isArray(this.voiceCall.asrSpeechParams)) {
      this.$set(this.voiceCall, 'asrSpeechParams', createDefaultSpeechParams())
    }
    if (!Array.isArray(this.voiceCall.flagshipTtsSpeechParams)) {
      this.$set(this.voiceCall, 'flagshipTtsSpeechParams', createDefaultSpeechParams())
    }
    if (!Array.isArray(this.voiceCall.flagshipAsrSpeechParams)) {
      this.$set(this.voiceCall, 'flagshipAsrSpeechParams', createDefaultSpeechParams())
    }
  },
  mounted() {
    this.syncPresetVoiceIdToEngine()
  },
  methods: {
    setPresetMode(mode) {
      const wasCustom = (this.voiceCall.presetMode || PRESET_MODE.ENGINE) === PRESET_MODE.CUSTOM
      this.$set(this.voiceCall, 'presetMode', mode)
      if (
        mode === PRESET_MODE.CUSTOM &&
        !wasCustom &&
        this.voiceCall.callControl &&
        typeof this.voiceCall.callControl === 'object'
      ) {
        this.$set(this.voiceCall.callControl, 'transition_sound_enabled', true)
      }
    },
    setServiceVersion(version) {
      const next = resolveServiceVersion({ serviceVersion: version, presetMode: this.voiceCall.presetMode })
      if (next === this.serviceVersion && this.voiceCall.serviceVersion === next) return
      this.$set(this.voiceCall, 'serviceVersion', next)
      if (next === SERVICE_VERSION.STANDARD) {
        this.setPresetMode(PRESET_MODE.CUSTOM)
        return
      }
      this.setPresetMode(PRESET_MODE.ENGINE)
      if (next === SERVICE_VERSION.FLAGSHIP) {
        this.ensureFlagshipAccess()
      }
    },
    ensureFlagshipAccess() {
      if (!this.voiceCall.flagshipAccess || typeof this.voiceCall.flagshipAccess !== 'object') {
        this.$set(this.voiceCall, 'flagshipAccess', createDefaultFlagshipAccess())
      }
    },
    applyStandardModel(modelId) {
      if (!this.voiceCall.tripleAccess || typeof this.voiceCall.tripleAccess !== 'object') {
        this.$set(this.voiceCall, 'tripleAccess', createDefaultTripleAccess())
      }
      applySystemLlmCatalogToTripleAccess(this.voiceCall.tripleAccess.llm, modelId)
    },
    syncPresetVoiceIdToEngine() {
      const list = this.presetVoices
      if (!list.length) return
      const ids = new Set(list.map((p) => p.id))
      const pv = String(this.voiceCall.presetVoiceId || '').trim()
      if (pv && ids.has(pv)) return
      const fallback = getDefaultPresetId(this.voiceCall.voiceEngine)
      const fallbackId = ids.has(fallback) ? fallback : list[0].id
      this.$set(this.voiceCall, 'presetVoiceId', fallbackId)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './shared.scss';

.vc-voice-tone {
  display: flex;
  flex-direction: column;
}

.vc-service-version-pin {
  position: sticky;
  top: 0;
  z-index: 8;
  margin: -16px -16px 18px;
  padding: 0 16px 16px;
  background: #f0f3f9;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: -16px;
    height: 16px;
    background: #f0f3f9;
  }

  > .vc-section {
    margin-bottom: 0;
  }
}

.vc-section {
  margin-bottom: 16px;
}

.vc-card {
  background: #fff;
  border: 1px solid #e8eaec;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  padding: 20px;
}

.vc-section-head {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  margin-bottom: 16px;

  &__icon {
    color: #595959;
    display: inline-flex;
  }

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: #262626;
    text-align: left;
    min-width: 0;
    flex: 0 1 auto;
  }

  &--model &__icon--setting {
    color: #5668f0;
  }

  &--voice &__icon--voice {
    color: #4b7fd4;
  }

  &__icon--voice .vc-voice-params-mic-icon {
    display: block;
  }

  &--access &__icon--access {
    color: #595959;
  }

  &--resource {
    margin-bottom: 20px;
  }

  &__icon--chip,
  &__icon--resource {
    width: 28px;
    height: 28px;
    margin-right: 6px;
    border-radius: 8px;
    color: #5c6fe6;
    background: #eef1ff;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
      display: block;
    }
  }
}

.vc-call-mode-cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.vc-call-mode-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 1px solid #e8eaec;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;

  &:hover:not(.vc-call-mode-card--active) {
    border-color: #c5c8ce;
    background: #fafafa;
  }

  &--active {
    border-color: #5668f0;
    background: #f5f7ff;
    box-shadow: 0 0 0 1px rgba(86, 104, 240, 0.15);
  }

  &__icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &--supreme {
      color: #fa541c;
      background: #fff2e8;
    }

    &--flagship {
      color: #722ed1;
      background: #f3ebff;
    }

    &--standard {
      color: #1677ff;
      background: #e6f4ff;
    }
  }

  &__text {
    flex: 1;
    min-width: 0;
  }

  &__title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 6px;
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: #262626;
  }

  &__desc {
    margin: 0;
    font-size: 12px;
    color: #909399;
    line-height: 1.5;
    text-align: left;
  }
}

@media (max-width: 960px) {
  .vc-call-mode-cards {
    grid-template-columns: 1fr;
  }
}

.vc-form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;

  &:last-of-type {
    margin-bottom: 0;
  }

  &--resource-line {
    flex-direction: row;
    align-items: center;
    gap: 16px;
    margin-bottom: 0;

    .vc-form-label {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      width: 42px;
      height: 32px;
      margin: 0;
      line-height: 32px;
    }

    .vc-form-control {
      flex: 1;
      min-width: 0;
      width: auto;
    }
  }
}

.vc-form-label {
  font-size: 14px;
  font-weight: 400;
  color: #606266;
  line-height: 1.4;
  text-align: left;

  &--required::before {
    content: '*';
    color: #ed4014;
    margin-right: 4px;
  }
}

.vc-form-control {
  width: 100%;

  ::v-deep &.ivu-select {
    width: 100%;
  }

  ::v-deep .ivu-select-selection {
    border-color: #dcdfe6;
    border-radius: 4px;
    text-align: left;
  }

  ::v-deep .ivu-select-selected-value {
    text-align: left;
  }

  ::v-deep .ivu-select-input {
    text-align: left;
  }

  ::v-deep .ivu-select-arrow {
    right: 18px;
  }
}

.vc-voice-select-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;

  .vc-form-control {
    flex: 1;
    min-width: 0;
  }
}
</style>

<style lang="scss">
/* 与音色等其它 Select 下拉保持同一浅色样式 */
.vc-model-select-transfer {
  .ivu-select-item {
    padding: 7px 30px !important;
  }
}
</style>

<style lang="scss">
.vc-voice-preset-select-transfer {
  .ivu-select-group-title {
    padding-left: 12px;
    font-size: 12px;
    font-weight: 400;
    color: #909399;
    line-height: 28px;
  }

  .ivu-select-item {
    padding: 7px 30px !important;
  }
}
</style>
